import {
  createFrontendPlugin,
  createRouteRef,
  PageBlueprint,
  ApiBlueprint,
} from '@backstage/frontend-plugin-api';
import { compatWrapper } from '@backstage/core-compat-api';
import {
  RadarPage,
  techRadarApiRef,
  type TechRadarApi,
} from '@backstage-community/plugin-tech-radar';
import type { TechRadarLoaderResponse } from '@backstage-community/plugin-tech-radar-common';
import { catalogApiRef, type CatalogApi } from '@backstage/plugin-catalog-react';
import MapIcon from '@material-ui/icons/MyLocation';
import { QUADRANTS, RINGS, radarEntries } from './techRadarFromCatalog';

// A API padrão do pacote (DefaultTechRadarApi) tenta buscar dados de um
// plugin de backend opcional (tech-radar-backend, não instalado aqui) e, se
// não encontrar, cai sozinha pra um dataset de EXEMPLO (JavaScript/React/
// Webpack, com descrições em Lorem Ipsum) - documentado no próprio código-
// fonte do plugin. Isso é enganoso (parece configuração real), por isso
// implementamos nossa própria TechRadarApi: mesmos anéis/quadrantes padrão,
// mas com itens REAIS tirados do catálogo (techRadarFromCatalog.ts): as
// tecnologias saem das tags dos Components (o anel vem do ciclo de vida das
// aplicações que as usam) e a infraestrutura, dos Resources da plataforma.
// Sem aplicações no catálogo, o radar mostra só a plataforma; nunca dados
// inventados.
class CatalogTechRadarApi implements TechRadarApi {
  constructor(private readonly catalogApi: CatalogApi) {}

  async load(): Promise<TechRadarLoaderResponse> {
    const { items } = await this.catalogApi.getEntities({
      filter: [{ kind: 'Component' }, { kind: 'Resource' }],
      fields: [
        'kind',
        'metadata.name',
        'metadata.title',
        'metadata.description',
        'metadata.tags',
        'spec.lifecycle',
        'spec.system',
      ],
    });
    const components = items
      .filter(entity => entity.kind === 'Component')
      .map(entity => ({
        name: entity.metadata.name,
        tags: entity.metadata.tags,
        lifecycle: typeof entity.spec?.lifecycle === 'string' ? entity.spec.lifecycle : undefined,
      }));
    const resources = items
      .filter(entity => entity.kind === 'Resource')
      .map(entity => ({
        name: entity.metadata.name,
        title: entity.metadata.title,
        description: entity.metadata.description,
        system: typeof entity.spec?.system === 'string' ? entity.spec.system : undefined,
      }));
    return { quadrants: QUADRANTS, rings: RINGS, entries: radarEntries(components, resources) };
  }
}

const techRadarApi = ApiBlueprint.make({
  name: 'tech-radar',
  params: define =>
    define({
      api: techRadarApiRef,
      deps: { catalogApi: catalogApiRef },
      factory: ({ catalogApi }) => new CatalogTechRadarApi(catalogApi),
    }),
});

const techRadarRouteRef = createRouteRef();

const techRadarPage = PageBlueprint.make({
  name: 'tech-radar',
  params: {
    path: '/tech-radar',
    title: 'Tech Radar',
    icon: <MapIcon />,
    routeRef: techRadarRouteRef,
    // TechRadarPage (export principal do pacote) e uma "routable extension"
    // do sistema antigo do Backstage - so funciona se descoberta pela arvore
    // de rotas antiga, nao pode ser renderizada dentro de um loader qualquer
    // (erro "was not discovered in the app element tree"). RadarPage e o
    // componente puro por baixo dela; compatWrapper (@backstage/core-compat-
    // api) da a ele o contexto de API legado que precisa pra funcionar fora
    // desse mecanismo - mesmo padrao usado no /alpha oficial do proprio
    // pacote.
    loader: async () =>
      compatWrapper(
        <RadarPage
          pageTitle="Tech Radar"
          subtitle="Tecnologias em uso nas aplicações do catálogo e serviços da plataforma"
          width={1500}
          height={800}
        />,
      ),
  },
});

export const techRadarPlugin = createFrontendPlugin({
  pluginId: 'tech-radar',
  extensions: [techRadarApi, techRadarPage],
});

export default techRadarPlugin;
